import {
  defaultFacelessImageStylePresetId,
  defaultFacelessMotionVideoModelId,
  defaultFacelessMotionVideoResolution,
  defaultMusicTrackId,
  defaultTtsModelId,
  defaultTtsVoiceId,
  openRouterTtsModelId,
  facelessImageStylePresets,
  facelessMotionVideoModelOptions,
  facelessMotionVideoResolutions,
  facelessScriptGenerationLimits,
  isFacelessMotionVideoModelId,
  isFacelessMotionVideoResolution,
  isEnabledTtsSelection,
  parseSceneDensity,
  sceneDensityLimits,
  type FacelessImageStylePresetId,
  type FacelessMotionVideoModelId,
  type FacelessMotionVideoResolution,
  type SceneDensity,
  type SceneVisualMode,
  type TtsModelId,
} from "@reelmagic/domain";

export type FacelessGeneratorDraft = {
  imageStylePresetId: FacelessImageStylePresetId;
  motionVideoModelId: FacelessMotionVideoModelId;
  motionVideoResolution: FacelessMotionVideoResolution;
  musicTrackId: string;
  openScriptGenerationModal: boolean;
  playbackSpeed: number;
  sceneDensity: SceneDensity;
  script: string;
  scriptDescription: string;
  targetDurationSeconds: number;
  title: string;
  ttsModelId: TtsModelId;
  visualMode: SceneVisualMode;
  voiceId: string;
};

export const facelessGeneratorDraftStorageKey =
  "reelmagic.facelessGeneratorDraft.v1";

const stylePresetValues = new Set<string>(
  facelessImageStylePresets.map((preset) => preset.id),
);
const motionVideoModelValues = new Set<string>(
  facelessMotionVideoModelOptions.map((option) => option.id),
);
const motionVideoResolutionValues = new Set<string>(
  facelessMotionVideoResolutions,
);
const visualModeValues = new Set<SceneVisualMode>(["image", "motion"]);

export const defaultFacelessGeneratorDraft: FacelessGeneratorDraft = {
  imageStylePresetId: defaultFacelessImageStylePresetId,
  motionVideoModelId: defaultFacelessMotionVideoModelId,
  motionVideoResolution: defaultFacelessMotionVideoResolution,
  musicTrackId: defaultMusicTrackId,
  openScriptGenerationModal: false,
  playbackSpeed: 1,
  sceneDensity: sceneDensityLimits.defaultSecondsPerScene,
  script: "",
  scriptDescription: "",
  targetDurationSeconds:
    facelessScriptGenerationLimits.defaultTargetDurationSeconds,
  title: "",
  ttsModelId: defaultTtsModelId,
  visualMode: "image",
  voiceId: defaultTtsVoiceId,
};

export function parseFacelessGeneratorDraft(
  value: unknown,
): FacelessGeneratorDraft | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const draft = value as Partial<Record<keyof FacelessGeneratorDraft, unknown>>;
  const title = typeof draft.title === "string" ? draft.title.slice(0, 120) : "";
  const script = typeof draft.script === "string" ? draft.script : "";
  const scriptDescription =
    typeof draft.scriptDescription === "string"
      ? draft.scriptDescription.slice(
          0,
          facelessScriptGenerationLimits.maxDescriptionLength,
        )
      : defaultFacelessGeneratorDraft.scriptDescription;
  const targetDurationSecondsRaw =
    typeof draft.targetDurationSeconds === "number"
      ? draft.targetDurationSeconds
      : defaultFacelessGeneratorDraft.targetDurationSeconds;
  const targetDurationSeconds = Number.isFinite(targetDurationSecondsRaw)
    ? Math.min(
        facelessScriptGenerationLimits.maxTargetDurationSeconds,
        Math.max(
          facelessScriptGenerationLimits.minTargetDurationSeconds,
          Math.round(targetDurationSecondsRaw),
        ),
      )
    : defaultFacelessGeneratorDraft.targetDurationSeconds;
  const requestedTtsModelId =
    typeof draft.ttsModelId === "string"
      ? draft.ttsModelId
      : isEnabledTtsSelection({ modelId: openRouterTtsModelId, voiceId: draft.voiceId })
        ? openRouterTtsModelId
        : defaultTtsModelId;
  const requestedVoiceId =
    typeof draft.voiceId === "string"
      ? draft.voiceId
      : defaultTtsVoiceId;
  const hasValidTtsSelection = isEnabledTtsSelection({
    modelId: requestedTtsModelId,
    voiceId: requestedVoiceId,
  });
  const ttsModelId = hasValidTtsSelection
    ? (requestedTtsModelId as TtsModelId)
    : defaultTtsModelId;
  const voiceId = hasValidTtsSelection
    ? requestedVoiceId
    : defaultTtsVoiceId;
  const sceneDensity =
    parseSceneDensity(draft.sceneDensity) ??
    defaultFacelessGeneratorDraft.sceneDensity;
  const imageStylePresetId =
    typeof draft.imageStylePresetId === "string" &&
    stylePresetValues.has(draft.imageStylePresetId as FacelessImageStylePresetId)
      ? (draft.imageStylePresetId as FacelessImageStylePresetId)
      : defaultFacelessGeneratorDraft.imageStylePresetId;
  const motionVideoModelId =
    typeof draft.motionVideoModelId === "string" &&
    motionVideoModelValues.has(draft.motionVideoModelId) &&
    isFacelessMotionVideoModelId(draft.motionVideoModelId)
      ? draft.motionVideoModelId
      : defaultFacelessGeneratorDraft.motionVideoModelId;
  const motionVideoResolution =
    typeof draft.motionVideoResolution === "string" &&
    motionVideoResolutionValues.has(draft.motionVideoResolution) &&
    isFacelessMotionVideoResolution(draft.motionVideoResolution)
      ? draft.motionVideoResolution
      : defaultFacelessGeneratorDraft.motionVideoResolution;
  const musicTrackId =
    typeof draft.musicTrackId === "string"
      ? draft.musicTrackId
      : defaultFacelessGeneratorDraft.musicTrackId;
  const openScriptGenerationModal =
    typeof draft.openScriptGenerationModal === "boolean"
      ? draft.openScriptGenerationModal
      : defaultFacelessGeneratorDraft.openScriptGenerationModal;
  const playbackSpeedRaw =
    typeof draft.playbackSpeed === "number"
      ? draft.playbackSpeed
      : defaultFacelessGeneratorDraft.playbackSpeed;
  const playbackSpeed = Number.isFinite(playbackSpeedRaw)
    ? Math.min(2.5, Math.max(1, Math.round(playbackSpeedRaw * 10) / 10))
    : defaultFacelessGeneratorDraft.playbackSpeed;
  const visualMode =
    typeof draft.visualMode === "string" &&
    visualModeValues.has(draft.visualMode as SceneVisualMode)
      ? (draft.visualMode as SceneVisualMode)
      : defaultFacelessGeneratorDraft.visualMode;

  return {
    imageStylePresetId,
    motionVideoModelId,
    motionVideoResolution,
    musicTrackId,
    openScriptGenerationModal,
    playbackSpeed,
    sceneDensity,
    script,
    scriptDescription,
    targetDurationSeconds,
    title,
    ttsModelId,
    visualMode,
    voiceId,
  };
}

export function readFacelessGeneratorDraft() {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(facelessGeneratorDraftStorageKey);
  if (!raw) {
    return null;
  }

  try {
    return parseFacelessGeneratorDraft(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeFacelessGeneratorDraft(draft: FacelessGeneratorDraft) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    facelessGeneratorDraftStorageKey,
    JSON.stringify(parseFacelessGeneratorDraft(draft) ?? defaultFacelessGeneratorDraft),
  );
}

export function clearFacelessGeneratorDraft() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(facelessGeneratorDraftStorageKey);
}
